import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from './firebase';
import './HOAllStudents.css';

function HOAllStudents({ onClose }) {
  const [students, setStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [franchisees, setFranchisees] = useState([]);
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [filterFranchisee, setFilterFranchisee] = useState('all');
  const [filterSchool, setFilterSchool] = useState('all');
  const [filterStatus, setFilterStatus] = useState('active');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('franchisee');
  const [filtersCollapsed, setFiltersCollapsed] = useState(false);
  
  // View mode: 'payments' or 'contacts'
  const [viewMode, setViewMode] = useState('payments');
  
  // Edit modal
  const [editingStudent, setEditingStudent] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  
  // T-shirt size edit
  const [editingTshirtStudent, setEditingTshirtStudent] = useState(null);
  const [editingTshirtSize, setEditingTshirtSize] = useState('');
  const [savingTshirt, setSavingTshirt] = useState(false);
  
  // Delete confirmation
  const [deletingStudent, setDeletingStudent] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    loadAllData();
  }, []);

  useEffect(() => {
    applyFiltersAndSort();
  }, [students, filterFranchisee, filterSchool, filterStatus, searchTerm, sortBy]);

  const loadAllData = async () => {
    setLoading(true);
    try {
      // Load all franchisees
      const franchiseesSnapshot = await getDocs(collection(db, 'Franchisees'));
      const franchiseesList = franchiseesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      franchiseesList.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setFranchisees(franchiseesList);

      // Create franchisee lookup
      const franchiseeMap = {};
      franchiseesList.forEach(f => {
        franchiseeMap[f.id] = f.name || 'Unknown';
      });

      // Load all schools
      const schoolsSnapshot = await getDocs(collection(db, 'schools'));
      const schoolsList = schoolsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setSchools(schoolsList);

      // Create school lookup
      const schoolMap = {};
      schoolsList.forEach(s => {
        schoolMap[s.id] = {
          name: s.schoolName || 'Unknown',
          franchiseeId: s.franchiseeRef?.id || null
        };
      });

      // Load all students
      const studentsSnapshot = await getDocs(collection(db, 'students'));
      const studentsList = studentsSnapshot.docs.map(doc => {
        const data = doc.data();
        const schoolId = data.schoolRef?.id;
        const schoolInfo = schoolMap[schoolId] || { name: 'Unknown', franchiseeId: null };
        const franchiseeId = data.franchiseeRef?.id || schoolInfo.franchiseeId;

        const termPayments = data.termPayments || {};

        return {
          id: doc.id,
          ...data,
          schoolId,
          schoolName: schoolInfo.name,
          franchiseeId,
          franchiseeName: franchiseeMap[franchiseeId] || 'Unknown',
          // Payment info
          adminPaid: termPayments.admin || 0,
          term1Paid: termPayments.term1 || 0,
          term2Paid: termPayments.term2 || 0,
          term3Paid: termPayments.term3 || 0,
          term4Paid: termPayments.term4 || 0,
          totalPaid: (termPayments.admin || 0) + (termPayments.term1 || 0) + 
                     (termPayments.term2 || 0) + (termPayments.term3 || 0) + (termPayments.term4 || 0),
          // Contact info
          primaryContact: data.primaryContact || {
            name: data.primaryContactName || '',
            email: data.primaryContactEmail || '',
            cellPhone: data.primaryContactCellPhone || '',
            homePhone: data.primaryContactHomePhone || '',
            workPhone: data.primaryContactWorkPhone || ''
          },
          secondaryContact: data.secondaryContact || {
            name: data.secondaryContactName || '',
            email: data.secondaryContactEmail || '',
            cellPhone: data.secondaryContactCellPhone || ''
          }
        };
      });

      setStudents(studentsList);
    } catch (error) {
      console.error('Error loading data:', error);
      alert('Error loading students data');
    } finally {
      setLoading(false);
    }
  };

  const applyFiltersAndSort = () => {
    let filtered = [...students];

    // Franchisee filter
    if (filterFranchisee !== 'all') {
      filtered = filtered.filter(s => s.franchiseeId === filterFranchisee);
    }

    // School filter
    if (filterSchool !== 'all') {
      filtered = filtered.filter(s => s.schoolId === filterSchool);
    }

    // Status filter
    if (filterStatus === 'active') {
      filtered = filtered.filter(s => s.isActive !== false);
    } else if (filterStatus === 'inactive') {
      filtered = filtered.filter(s => s.isActive === false);
    }

    // Search filter
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(s => {
        const name = (s.studentName || '').toLowerCase();
        const account = (s.accountNumber || '').toLowerCase();
        const parentName = (s.primaryContact?.name || '').toLowerCase();
        const parentEmail = (s.primaryContact?.email || '').toLowerCase();
        return name.includes(search) || account.includes(search) || 
               parentName.includes(search) || parentEmail.includes(search);
      });
    }

    // Sort
    switch (sortBy) {
      case 'franchisee':
        filtered.sort((a, b) => {
          const fCompare = (a.franchiseeName || '').localeCompare(b.franchiseeName || '');
          if (fCompare !== 0) return fCompare;
          const sCompare = (a.schoolName || '').localeCompare(b.schoolName || '');
          if (sCompare !== 0) return sCompare;
          return (a.studentName || '').localeCompare(b.studentName || '');
        });
        break;
      case 'school':
        filtered.sort((a, b) => {
          const sCompare = (a.schoolName || '').localeCompare(b.schoolName || '');
          if (sCompare !== 0) return sCompare;
          return (a.studentName || '').localeCompare(b.studentName || '');
        });
        break;
      case 'name':
        filtered.sort((a, b) => (a.studentName || '').localeCompare(b.studentName || ''));
        break;
      case 'total-desc':
        filtered.sort((a, b) => b.totalPaid - a.totalPaid);
        break;
      default:
        break;
    }

    setFilteredStudents(filtered);
  };

  // Get filtered schools based on franchisee selection
  const getFilteredSchools = () => {
    if (filterFranchisee === 'all') return schools;
    return schools.filter(s => s.franchiseeRef?.id === filterFranchisee);
  };

  const formatCurrency = (amount) => `R${(amount || 0).toFixed(2)}`;

  // Calculate totals
  const getTotals = () => {
    return filteredStudents.reduce((totals, s) => ({
      adminPaid: totals.adminPaid + s.adminPaid,
      term1Paid: totals.term1Paid + s.term1Paid,
      term2Paid: totals.term2Paid + s.term2Paid,
      term3Paid: totals.term3Paid + s.term3Paid,
      term4Paid: totals.term4Paid + s.term4Paid,
      totalPaid: totals.totalPaid + s.totalPaid
    }), {
      adminPaid: 0, term1Paid: 0, term2Paid: 0, term3Paid: 0, term4Paid: 0, totalPaid: 0
    });
  };

  // Normalize t-shirt size — some older records store "4-5" instead of "4-5 YEARS".
  // This prevents Excel from auto-converting sizes like "4-5" into dates ("5-Apr").
  const normalizeTshirtSize = (size) => {
    if (!size || size === '-') return size || '-';
    const childSizePattern = /^\d+-\d+$/; // matches "4-5", "7-8" etc
    if (childSizePattern.test(size.trim())) {
      return `${size.trim()} YEARS`;
    }
    return size;
  };

  // Export to CSV
  const exportToCSV = () => {
    let headers, rows;

    if (viewMode === 'payments') {
      headers = ['Franchisee', 'School', 'Student Name', 'Account #', 'Status', 'Admin', 'Term 1', 'Term 2', 'Term 3', 'Term 4', 'Total Paid'];
      rows = filteredStudents.map(s => [
        s.franchiseeName, s.schoolName, s.studentName, s.accountNumber || '',
        s.isActive !== false ? 'Active' : 'Inactive',
        s.adminPaid.toFixed(2), s.term1Paid.toFixed(2), s.term2Paid.toFixed(2),
        s.term3Paid.toFixed(2), s.term4Paid.toFixed(2), s.totalPaid.toFixed(2)
      ]);
    } else if (viewMode === 'tshirts') {
      headers = ['Franchisee', 'School', 'Student Name', 'Admin Fee Paid', 'T-Shirt Ordered', 'Size', 'Source', 'Date'];
      rows = filteredStudents.map(s => {
        const adminFeeRequired = s.adminFee || 0;
        const adminFeePaid = s.adminPaid || 0;
        const adminPaidStatus = adminFeeRequired > 0
          ? (adminFeePaid >= adminFeeRequired ? 'Paid' : `Unpaid (R${adminFeePaid}/R${adminFeeRequired})`)
          : '-';
        const ordered = s.tshirtOrdered === true ? 'Yes' : 'No';
        const size = normalizeTshirtSize(s.tShirtSize);
        const source = s.tshirtSource === 'stock' ? 'Stock Allocation'
          : s.tshirtOrdered === true ? 'New Order' : '-';
        let date = '-';
        if (s.tshirtSource === 'stock' && s.stockAllocationDate) {
          const d = s.stockAllocationDate.toDate ? s.stockAllocationDate.toDate() : new Date(s.stockAllocationDate);
          date = d.toLocaleDateString('en-ZA');
        } else if (s.tshirtOrderDate) {
          const d = s.tshirtOrderDate.toDate ? s.tshirtOrderDate.toDate() : new Date(s.tshirtOrderDate);
          date = d.toLocaleDateString('en-ZA');
        }
        return [s.franchiseeName, s.schoolName, s.studentName, adminPaidStatus, ordered, size, source, date];
      });
    } else {
      headers = ['Franchisee', 'School', 'Student Name', 'Parent Name', 'Email', 'Cell Phone', 'Home Phone', 'Work Phone'];
      rows = filteredStudents.map(s => [
        s.franchiseeName, s.schoolName, s.studentName,
        s.primaryContact?.name || '', s.primaryContact?.email || '',
        s.primaryContact?.cellPhone || '', s.primaryContact?.homePhone || '',
        s.primaryContact?.workPhone || ''
      ]);
    }

    const csvContent = [
      headers.join(','),
      ...rows.map((row, rowIdx) => row.map((cell, colIdx) => {
        // For the tshirts view, the Size column (index 5) needs special treatment
        // to stop Excel auto-converting values like "4-5" or "4-5 YEARS" into dates.
        // Using ="value" forces Excel to treat the cell as plain text.
        if (viewMode === 'tshirts' && colIdx === 5 && cell && cell !== '-') {
          return `"=""${cell}"""`;
        }
        return `"${cell || ''}"`;
      }).join(','))
    ].join('\n');

    // Add UTF-8 BOM so Excel opens the file correctly
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ho-students-${viewMode}-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Export emails for newsletter
  const exportEmails = () => {
    const emails = filteredStudents
      .map(s => s.primaryContact?.email)
      .filter(email => email && email.trim())
      .filter((email, index, self) => self.indexOf(email) === index); // Remove duplicates

    const content = emails.join('\n');
    const blob = new Blob([content], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `parent-emails-${new Date().toISOString().split('T')[0]}.txt`;
    a.click();
    window.URL.revokeObjectURL(url);

    alert(`Exported ${emails.length} unique email addresses`);
  };

  // T-shirt size edit
  const openTshirtSizeEdit = (student) => {
    setEditingTshirtStudent(student);
    setEditingTshirtSize(normalizeTshirtSize(student.tShirtSize) || '');
  };

  const saveTshirtSize = async () => {
    if (!editingTshirtStudent) return;
    setSavingTshirt(true);
    try {
      await updateDoc(doc(db, 'students', editingTshirtStudent.id), {
        tShirtSize: editingTshirtSize,
        updatedAt: new Date()
      });
      // Update local state so the table refreshes immediately without a full reload
      setStudents(prev => prev.map(s =>
        s.id === editingTshirtStudent.id
          ? { ...s, tShirtSize: editingTshirtSize }
          : s
      ));
      setEditingTshirtStudent(null);
      setEditingTshirtSize('');
    } catch (error) {
      console.error('Error saving t-shirt size:', error);
      alert('Error saving size. Please try again.');
    } finally {
      setSavingTshirt(false);
    }
  };

  // Edit student
  const openEditModal = (student) => {
    setEditingStudent(student);
    setEditForm({
      studentName: student.studentName || '',
      accountNumber: student.accountNumber || '',
      class: student.class || '',
      adminFee: student.adminFee || 0,
      primaryContactName: student.primaryContact?.name || '',
      primaryContactEmail: student.primaryContact?.email || '',
      primaryContactCellPhone: student.primaryContact?.cellPhone || '',
      primaryContactHomePhone: student.primaryContact?.homePhone || '',
      primaryContactWorkPhone: student.primaryContact?.workPhone || '',
      // Payment overrides
      adminPaid: student.termPayments?.admin || 0,
      term1Paid: student.termPayments?.term1 || 0,
      term2Paid: student.termPayments?.term2 || 0,
      term3Paid: student.termPayments?.term3 || 0,
      term4Paid: student.termPayments?.term4 || 0
    });
  };

  const handleSaveEdit = async () => {
    if (!editingStudent) return;
    setSaving(true);

    try {
      const studentRef = doc(db, 'students', editingStudent.id);
      
      await updateDoc(studentRef, {
        studentName: editForm.studentName,
        accountNumber: editForm.accountNumber,
        class: editForm.class,
        adminFee: parseFloat(editForm.adminFee) || 0,
        primaryContact: {
          name: editForm.primaryContactName,
          email: editForm.primaryContactEmail,
          cellPhone: editForm.primaryContactCellPhone,
          homePhone: editForm.primaryContactHomePhone,
          workPhone: editForm.primaryContactWorkPhone
        },
        // Also update legacy fields for compatibility
        primaryContactName: editForm.primaryContactName,
        primaryContactEmail: editForm.primaryContactEmail,
        primaryContactCellPhone: editForm.primaryContactCellPhone,
        primaryContactHomePhone: editForm.primaryContactHomePhone,
        primaryContactWorkPhone: editForm.primaryContactWorkPhone,
        // Update payments
        termPayments: {
          admin: parseFloat(editForm.adminPaid) || 0,
          term1: parseFloat(editForm.term1Paid) || 0,
          term2: parseFloat(editForm.term2Paid) || 0,
          term3: parseFloat(editForm.term3Paid) || 0,
          term4: parseFloat(editForm.term4Paid) || 0
        }
      });

      alert('Student updated successfully!');
      setEditingStudent(null);
      loadAllData(); // Reload data
    } catch (error) {
      console.error('Error updating student:', error);
      alert('Error updating student. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Delete student
  const handleDelete = async () => {
    if (!deletingStudent) return;
    setDeleting(true);

    try {
      await deleteDoc(doc(db, 'students', deletingStudent.id));
      alert('Student deleted successfully!');
      setDeletingStudent(null);
      loadAllData(); // Reload data
    } catch (error) {
      console.error('Error deleting student:', error);
      alert('Error deleting student. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  const totals = getTotals();

  if (loading) {
    return <div className="ho-students-loading">Loading all students...</div>;
  }

  return (
    <div className="ho-students-container">
      <div className="ho-students-header">
        <h1>🎓 All Students</h1>
        <p className="subtitle">View and manage all students across all franchisees</p>
      </div>

      {/* View Mode Toggle */}
      <div className="view-mode-toggle">
        <button 
          className={viewMode === 'payments' ? 'active' : ''}
          onClick={() => setViewMode('payments')}
        >
          💰 Payment Info
        </button>
        <button 
          className={viewMode === 'contacts' ? 'active' : ''}
          onClick={() => setViewMode('contacts')}
        >
          📧 Contact Details
        </button>
        <button 
          className={viewMode === 'tshirts' ? 'active' : ''}
          onClick={() => setViewMode('tshirts')}
        >
          👕 T-Shirt Sizes
        </button>
        <button onClick={onClose} className="back-btn">← Back</button>
      </div>

      {/* Filters */}
      <div className="filters-section">
        <button 
          className="toggle-filters-btn"
          onClick={() => setFiltersCollapsed(!filtersCollapsed)}
        >
          {filtersCollapsed ? '▼ Show Filters' : '▲ Hide Filters'}
        </button>

        {!filtersCollapsed && (
          <div className="filters-grid">
            <div className="filter-group">
              <label>Franchisee:</label>
              <select 
                value={filterFranchisee} 
                onChange={(e) => {
                  setFilterFranchisee(e.target.value);
                  setFilterSchool('all'); // Reset school filter
                }}
              >
                <option value="all">All Franchisees</option>
                {franchisees.map(f => (
                  <option key={f.id} value={f.id}>{f.name}</option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label>School:</label>
              <select value={filterSchool} onChange={(e) => setFilterSchool(e.target.value)}>
                <option value="all">All Schools</option>
                {getFilteredSchools().map(s => (
                  <option key={s.id} value={s.id}>{s.schoolName}</option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label>Status:</label>
              <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                <option value="active">Active Only</option>
                <option value="inactive">Inactive Only</option>
                <option value="all">All Students</option>
              </select>
            </div>

            <div className="filter-group">
              <label>Sort By:</label>
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                <option value="franchisee">Franchisee → School → Name</option>
                <option value="school">School → Name</option>
                <option value="name">Student Name</option>
                <option value="total-desc">Highest Payment First</option>
              </select>
            </div>

            <div className="filter-group search-group">
              <label>Search:</label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Name, account, parent, email..."
              />
            </div>

            <div className="filter-group export-group">
              <label>&nbsp;</label>
              <div className="export-buttons">
                <button onClick={exportToCSV} className="btn-export">
                  📥 Export CSV
                </button>
                {viewMode === 'contacts' && (
                  <button onClick={exportEmails} className="btn-export-emails">
                    📧 Export Emails
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Summary Bar */}
        <div className="summary-bar">
          <div className="summary-item">
            <span className="label">Students:</span>
            <span className="value">{filteredStudents.length}</span>
          </div>
          {viewMode === 'payments' && (
            <div className="summary-item highlight">
              <span className="label">Total Received:</span>
              <span className="value">{formatCurrency(totals.totalPaid)}</span>
            </div>
          )}
          {viewMode === 'contacts' && (
            <div className="summary-item highlight">
              <span className="label">Emails:</span>
              <span className="value">
                {filteredStudents.filter(s => s.primaryContact?.email).length}
              </span>
            </div>
          )}
          {viewMode === 'tshirts' && (
            <>
              <div className="summary-item highlight">
                <span className="label">T-Shirts Ordered:</span>
                <span className="value">
                  {filteredStudents.filter(s => s.tshirtOrdered === true).length}
                </span>
              </div>
              <div className="summary-item">
                <span className="label">Not Yet Ordered:</span>
                <span className="value">
                  {filteredStudents.filter(s => s.tshirtOrdered !== true).length}
                </span>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Data Table */}
      {filteredStudents.length === 0 ? (
        <div className="no-results">
          <p>No students found matching your filters.</p>
        </div>
      ) : (
        <div className="table-container">
          {viewMode === 'payments' ? (
            // PAYMENTS VIEW
            <table className="students-table">
              <thead>
                <tr>
                  <th>Franchisee</th>
                  <th>School</th>
                  <th>Student</th>
                  <th>Account #</th>
                  <th className="amount">Admin</th>
                  <th className="amount">Term 1</th>
                  <th className="amount">Term 2</th>
                  <th className="amount">Term 3</th>
                  <th className="amount">Term 4</th>
                  <th className="amount total">Total</th>
                  <th className="actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredStudents.map((student, idx) => {
                  const isNewFranchisee = idx === 0 || 
                    filteredStudents[idx - 1].franchiseeId !== student.franchiseeId;
                  const isNewSchool = idx === 0 || 
                    filteredStudents[idx - 1].schoolId !== student.schoolId;

                  return (
                    <tr key={student.id} className={student.isActive === false ? 'inactive' : ''}>
                      <td className="franchisee-cell">
                        {sortBy === 'franchisee' && !isNewFranchisee ? '' : student.franchiseeName}
                      </td>
                      <td className="school-cell">
                        {(sortBy === 'franchisee' || sortBy === 'school') && !isNewSchool ? '' : student.schoolName}
                      </td>
                      <td>
                        <span className="student-name">{student.studentName}</span>
                        {student.isActive === false && <span className="inactive-badge">Inactive</span>}
                      </td>
                      <td className="account-cell">{student.accountNumber}</td>
                      <td className={`amount ${student.adminPaid > 0 ? 'paid' : 'zero'}`}>
                        {formatCurrency(student.adminPaid)}
                      </td>
                      <td className={`amount ${student.term1Paid > 0 ? 'paid' : 'zero'}`}>
                        {formatCurrency(student.term1Paid)}
                      </td>
                      <td className={`amount ${student.term2Paid > 0 ? 'paid' : 'zero'}`}>
                        {formatCurrency(student.term2Paid)}
                      </td>
                      <td className={`amount ${student.term3Paid > 0 ? 'paid' : 'zero'}`}>
                        {formatCurrency(student.term3Paid)}
                      </td>
                      <td className={`amount ${student.term4Paid > 0 ? 'paid' : 'zero'}`}>
                        {formatCurrency(student.term4Paid)}
                      </td>
                      <td className={`amount total ${student.totalPaid > 0 ? 'paid' : 'zero'}`}>
                        {formatCurrency(student.totalPaid)}
                      </td>
                      <td className="actions">
                        <button onClick={() => openEditModal(student)} className="btn-edit" title="Edit">
                          ✏️
                        </button>
                        <button onClick={() => setDeletingStudent(student)} className="btn-delete" title="Delete">
                          🗑️
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="totals-row">
                  <td colSpan="4"><strong>TOTALS:</strong></td>
                  <td className="amount paid"><strong>{formatCurrency(totals.adminPaid)}</strong></td>
                  <td className="amount paid"><strong>{formatCurrency(totals.term1Paid)}</strong></td>
                  <td className="amount paid"><strong>{formatCurrency(totals.term2Paid)}</strong></td>
                  <td className="amount paid"><strong>{formatCurrency(totals.term3Paid)}</strong></td>
                  <td className="amount paid"><strong>{formatCurrency(totals.term4Paid)}</strong></td>
                  <td className="amount total paid"><strong>{formatCurrency(totals.totalPaid)}</strong></td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          ) : viewMode === 'tshirts' ? (
            // T-SHIRTS VIEW
            <table className="students-table">
              <thead>
                <tr>
                  <th>Franchisee</th>
                  <th>School</th>
                  <th>Student</th>
                  <th className="amount">Admin Fee</th>
                  <th className="amount">T-Shirt</th>
                  <th className="amount">Size</th>
                  <th className="amount">Source</th>
                  <th className="amount">Date</th>
                </tr>
              </thead>
              <tbody>
                {filteredStudents.map((student, idx) => {
                  const isNewFranchisee = idx === 0 ||
                    filteredStudents[idx - 1].franchiseeId !== student.franchiseeId;
                  const isNewSchool = idx === 0 ||
                    filteredStudents[idx - 1].schoolId !== student.schoolId;

                  const ordered = student.tshirtOrdered === true;
                  const source = student.tshirtSource === 'stock' ? '📦 Stock'
                    : ordered ? '🆕 New Order' : '-';
                  const sourceColor = student.tshirtSource === 'stock' ? '#2196F3'
                    : ordered ? '#4CAF50' : '#999';

                  // Admin fee paid check
                  const adminFeeRequired = student.adminFee || 0;
                  const adminFeePaid = student.adminPaid || 0;
                  const adminPaid = adminFeeRequired > 0 && adminFeePaid >= adminFeeRequired;

                  let dateDisplay = '-';
                  if (student.tshirtSource === 'stock' && student.stockAllocationDate) {
                    const d = student.stockAllocationDate.toDate
                      ? student.stockAllocationDate.toDate()
                      : new Date(student.stockAllocationDate);
                    dateDisplay = d.toLocaleDateString('en-ZA');
                  } else if (student.tshirtOrderDate) {
                    const d = student.tshirtOrderDate.toDate
                      ? student.tshirtOrderDate.toDate()
                      : new Date(student.tshirtOrderDate);
                    dateDisplay = d.toLocaleDateString('en-ZA');
                  }

                  return (
                    <tr key={student.id} className={student.isActive === false ? 'inactive' : ''}>
                      <td className="franchisee-cell">
                        {sortBy === 'franchisee' && !isNewFranchisee ? '' : student.franchiseeName}
                      </td>
                      <td className="school-cell">
                        {(sortBy === 'franchisee' || sortBy === 'school') && !isNewSchool ? '' : student.schoolName}
                      </td>
                      <td>
                        <span className="student-name">{student.studentName}</span>
                        {student.isActive === false && <span className="inactive-badge">Inactive</span>}
                      </td>
                      <td className="amount">
                        {adminFeeRequired > 0 ? (
                          <span style={{
                            display: 'inline-block',
                            padding: '2px 8px',
                            borderRadius: '12px',
                            fontSize: '12px',
                            fontWeight: '600',
                            background: adminPaid ? '#e8f5e9' : '#fce4e4',
                            color: adminPaid ? '#2e7d32' : '#c62828'
                          }}>
                            {adminPaid ? `✅ R${adminFeePaid}` : `❌ R${adminFeePaid} / R${adminFeeRequired}`}
                          </span>
                        ) : '-'}
                      </td>
                      <td className="amount">
                        <span style={{
                          fontWeight: '600',
                          color: ordered ? '#4CAF50' : '#999'
                        }}>
                          {ordered ? '✅ Yes' : '⏳ No'}
                        </span>
                      </td>
                      <td className="amount">
                        {student.tShirtSize ? (
                          <span style={{
                            background: '#e8f5e9',
                            color: '#2e7d32',
                            padding: '2px 8px',
                            borderRadius: '12px',
                            fontSize: '12px',
                            fontWeight: '600'
                          }}>
                            {normalizeTshirtSize(student.tShirtSize)}
                          </span>
                        ) : '-'}
                        <button
                          onClick={() => openTshirtSizeEdit(student)}
                          title="Edit T-shirt size"
                          style={{
                            marginLeft: '6px',
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: '14px',
                            opacity: 0.6,
                            verticalAlign: 'middle'
                          }}
                        >✏️</button>
                      </td>
                      <td className="amount">
                        <span style={{ color: sourceColor, fontSize: '13px', fontWeight: '500' }}>
                          {source}
                        </span>
                      </td>
                      <td className="amount" style={{ fontSize: '13px', color: '#666' }}>
                        {dateDisplay}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            // CONTACTS VIEW
            <table className="students-table contacts-table">
              <thead>
                <tr>
                  <th>Franchisee</th>
                  <th>School</th>
                  <th>Student</th>
                  <th>Parent Name</th>
                  <th>Email</th>
                  <th>Cell Phone</th>
                  <th>Home Phone</th>
                  <th className="actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredStudents.map((student, idx) => {
                  const isNewFranchisee = idx === 0 || 
                    filteredStudents[idx - 1].franchiseeId !== student.franchiseeId;
                  const isNewSchool = idx === 0 || 
                    filteredStudents[idx - 1].schoolId !== student.schoolId;

                  return (
                    <tr key={student.id} className={student.isActive === false ? 'inactive' : ''}>
                      <td className="franchisee-cell">
                        {sortBy === 'franchisee' && !isNewFranchisee ? '' : student.franchiseeName}
                      </td>
                      <td className="school-cell">
                        {(sortBy === 'franchisee' || sortBy === 'school') && !isNewSchool ? '' : student.schoolName}
                      </td>
                      <td>
                        <span className="student-name">{student.studentName}</span>
                        {student.isActive === false && <span className="inactive-badge">Inactive</span>}
                      </td>
                      <td>{student.primaryContact?.name || '-'}</td>
                      <td className="email-cell">
                        {student.primaryContact?.email ? (
                          <a href={`mailto:${student.primaryContact.email}`}>
                            {student.primaryContact.email}
                          </a>
                        ) : '-'}
                      </td>
                      <td>{student.primaryContact?.cellPhone || '-'}</td>
                      <td>{student.primaryContact?.homePhone || '-'}</td>
                      <td className="actions">
                        <button onClick={() => openEditModal(student)} className="btn-edit" title="Edit">
                          ✏️
                        </button>
                        <button onClick={() => setDeletingStudent(student)} className="btn-delete" title="Delete">
                          🗑️
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Edit Modal */}
      {editingStudent && (
        <div className="modal-overlay">
          <div className="edit-modal">
            <h2>✏️ Edit Student</h2>
            <p className="edit-context">
              {editingStudent.franchiseeName} → {editingStudent.schoolName}
            </p>

            <div className="edit-form">
              <div className="form-section">
                <h3>Student Information</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label>Student Name:</label>
                    <input
                      type="text"
                      value={editForm.studentName}
                      onChange={(e) => setEditForm({...editForm, studentName: e.target.value})}
                    />
                  </div>
                  <div className="form-group">
                    <label>Account Number:</label>
                    <input
                      type="text"
                      value={editForm.accountNumber}
                      onChange={(e) => setEditForm({...editForm, accountNumber: e.target.value})}
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Class:</label>
                    <input
                      type="text"
                      value={editForm.class}
                      onChange={(e) => setEditForm({...editForm, class: e.target.value})}
                    />
                  </div>
                  <div className="form-group">
                    <label>Admin Fee:</label>
                    <input
                      type="number"
                      value={editForm.adminFee}
                      onChange={(e) => setEditForm({...editForm, adminFee: e.target.value})}
                    />
                  </div>
                </div>
              </div>

              <div className="form-section">
                <h3>Primary Contact</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label>Name:</label>
                    <input
                      type="text"
                      value={editForm.primaryContactName}
                      onChange={(e) => setEditForm({...editForm, primaryContactName: e.target.value})}
                    />
                  </div>
                  <div className="form-group">
                    <label>Email:</label>
                    <input
                      type="email"
                      value={editForm.primaryContactEmail}
                      onChange={(e) => setEditForm({...editForm, primaryContactEmail: e.target.value})}
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Cell Phone:</label>
                    <input
                      type="text"
                      value={editForm.primaryContactCellPhone}
                      onChange={(e) => setEditForm({...editForm, primaryContactCellPhone: e.target.value})}
                    />
                  </div>
                  <div className="form-group">
                    <label>Home Phone:</label>
                    <input
                      type="text"
                      value={editForm.primaryContactHomePhone}
                      onChange={(e) => setEditForm({...editForm, primaryContactHomePhone: e.target.value})}
                    />
                  </div>
                </div>
              </div>

              <div className="form-section">
                <h3>💰 Payment Corrections</h3>
                <p className="warning-text">⚠️ Only edit these to fix incorrect data entries</p>
                <div className="form-row payments-row">
                  <div className="form-group small">
                    <label>Admin:</label>
                    <input
                      type="number"
                      value={editForm.adminPaid}
                      onChange={(e) => setEditForm({...editForm, adminPaid: e.target.value})}
                    />
                  </div>
                  <div className="form-group small">
                    <label>Term 1:</label>
                    <input
                      type="number"
                      value={editForm.term1Paid}
                      onChange={(e) => setEditForm({...editForm, term1Paid: e.target.value})}
                    />
                  </div>
                  <div className="form-group small">
                    <label>Term 2:</label>
                    <input
                      type="number"
                      value={editForm.term2Paid}
                      onChange={(e) => setEditForm({...editForm, term2Paid: e.target.value})}
                    />
                  </div>
                  <div className="form-group small">
                    <label>Term 3:</label>
                    <input
                      type="number"
                      value={editForm.term3Paid}
                      onChange={(e) => setEditForm({...editForm, term3Paid: e.target.value})}
                    />
                  </div>
                  <div className="form-group small">
                    <label>Term 4:</label>
                    <input
                      type="number"
                      value={editForm.term4Paid}
                      onChange={(e) => setEditForm({...editForm, term4Paid: e.target.value})}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-actions">
              <button onClick={handleSaveEdit} className="btn-save" disabled={saving}>
                {saving ? 'Saving...' : '💾 Save Changes'}
              </button>
              <button onClick={() => setEditingStudent(null)} className="btn-cancel" disabled={saving}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deletingStudent && (
        <div className="modal-overlay">
          <div className="delete-modal">
            <h2>⚠️ Delete Student</h2>
            <p>Are you sure you want to permanently delete:</p>
            <div className="delete-student-info">
              <strong>{deletingStudent.studentName}</strong>
              <span>{deletingStudent.schoolName}</span>
              <span>{deletingStudent.franchiseeName}</span>
            </div>
            <p className="delete-warning">
              This action cannot be undone. All payment history will be lost.
            </p>
            <div className="modal-actions">
              <button onClick={handleDelete} className="btn-delete-confirm" disabled={deleting}>
                {deleting ? 'Deleting...' : '🗑️ Yes, Delete'}
              </button>
              <button onClick={() => setDeletingStudent(null)} className="btn-cancel" disabled={deleting}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      {/* T-Shirt Size Edit Modal */}
      {editingTshirtStudent && (
        <div className="modal-overlay">
          <div className="edit-modal" style={{ maxWidth: '420px' }}>
            <h2>👕 Edit T-Shirt Size</h2>
            <p className="edit-context">
              {editingTshirtStudent.franchiseeName} → {editingTshirtStudent.schoolName}
            </p>
            <div className="edit-form">
              <div className="form-section">
                <div className="form-row">
                  <div className="form-group" style={{ width: '100%' }}>
                    <label style={{ fontWeight: '600', marginBottom: '6px', display: 'block' }}>
                      Student:
                    </label>
                    <div style={{
                      padding: '10px',
                      background: '#f8f9fa',
                      borderRadius: '6px',
                      fontWeight: '600',
                      fontSize: '15px',
                      marginBottom: '16px'
                    }}>
                      {editingTshirtStudent.studentName}
                    </div>
                    <label style={{ fontWeight: '600', marginBottom: '6px', display: 'block' }}>
                      T-Shirt Size:
                    </label>
                    <select
                      value={editingTshirtSize}
                      onChange={(e) => setEditingTshirtSize(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '10px',
                        borderRadius: '6px',
                        border: '2px solid #667eea',
                        fontSize: '15px',
                        fontWeight: '600'
                      }}
                    >
                      <option value="">-- Select Size --</option>
                      <option value="2-3 YEARS">2-3 YEARS</option>
                      <option value="4-5 YEARS">4-5 YEARS</option>
                      <option value="5-6 YEARS">5-6 YEARS</option>
                      <option value="7-8 YEARS">7-8 YEARS</option>
                      <option value="9-10 YEARS">9-10 YEARS</option>
                      <option value="11-12 YEARS">11-12 YEARS</option>
                      <option value="13-14 YEARS">13-14 YEARS</option>
                      <option value="S">S</option>
                      <option value="M">M</option>
                      <option value="L">L</option>
                    </select>
                    {editingTshirtStudent.tshirtOrdered && (
                      <p style={{
                        marginTop: '12px',
                        padding: '10px',
                        background: '#fff3cd',
                        borderRadius: '6px',
                        fontSize: '13px',
                        color: '#856404'
                      }}>
                        ⚠️ This student already has a shirt ordered. Changing the size here only updates the student record — it does not change the order document.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-actions">
              <button
                onClick={saveTshirtSize}
                className="btn-save"
                disabled={savingTshirt || !editingTshirtSize}
              >
                {savingTshirt ? 'Saving...' : '💾 Save Size'}
              </button>
              <button
                onClick={() => { setEditingTshirtStudent(null); setEditingTshirtSize(''); }}
                className="btn-cancel"
                disabled={savingTshirt}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default HOAllStudents;